/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState, useMemo, useContext } from 'react';
import { Container, CircularProgress, Typography, Button, Box, Grid2 } from '@mui/material';
import { withStyles } from '@mui/styles';
import { FormProvider, useFieldArray, useForm, Controller } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import FlagIcon from '@mui/icons-material/Flag';
import { Alert as MuiAlert } from '@mui/lab';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import ExpenseButtons from '../Components/Expense/ExpenseButtons';
import ExpenseReceiptUploadForm from '../Components/Expense/ExpenseReceiptUploadForm';
import ExpenseReceiptUpload from '../Components/Expense/ExpenseReceiptUpload';
import ExpenseEntryHeader from '../Components/Expense/ExpenseEntryHeader';
import {
    createExpense,
    getExpenseById,
    getExpenseCategories,
    getStatusesAllowed,
    updateExpense,
    patchExpenseCreditMemo,
} from '../Services/ExpenseService';
import { getDivisions } from '../Services/Common';
import { formatErrorMessage } from '../Utils/ErrorMessage';
import Alert from '../Components/Alert';
import ExpenseTabs from '../Components/Expense/Tabs/ExpenseTabs';
import AddExpenseTab from '../Components/Expense/Tabs/AddExpenseTab';
import ExpenseTabPanel from '../Components/Expense/Tabs/ExpenseTabPanel';
import BookingDetailsForm from '../Components/Expense/BookingDetailsForm';
import ExpenseSalesforceActionButton from '../Components/Expense/ExpenseSalesforceActionButton';
import InvoicePriorityIndicator from '../Components/Expense/InvoicePriorityIndicator';
import ExpenseSummary from '../Components/Expense/ExpenseSummary';
import InternalNotesForm from '../Components/Expense/InternalNotesForm';
import ReasonForSplittingForm from '../Components/Expense/ReasonForSplittingForm';
import ExpenseNeedsReviewAlert from '../Components/Expense/ExpenseNeedsReviewAlert';
import { COLORS } from '../../utils/Application_Constants';
import VendorExpenseDetailsForm from '../Components/Expense/VendorExpenseDetailsForm';
import PaymentDetailsForm from '../Components/Expense/PaymentDetailsForm';
import ExpenseTab from '../Components/Expense/Tabs/ExpenseTab';
import ExpenseTabLabel from '../Components/Expense/Tabs/ExpenseTabLabel';
import { AdminAuthContext } from '../../Admin/Contexts/AdminAuthContext';
import {
    createExpenseFromExpenseEntries,
    setStatusForEntries,
    isExpenseViewOnlyByStatus,
    isExpenseInInvoiceReview,
    createCorrectionExpenseFromEntries,
    handleSplitExpenseDeltas,
} from '../Utils/ExpenseUtil';
import { uploadImages } from '../Utils/UploadImages';
import { createExpenseDelta } from '../Utils/ExpenseDelta';
import { currencies } from '../Configurations/ExpenseEntryConfig';
import { ExpenseClassEnum } from '../Components/Expense/ExpenseClass.enum';
import { ValidationModeContext } from '../../contexts/VendorPaymentValidationModeContext';
import {
    ExpenseStatusIdsEnum,
    ExpenseStatusValuesEnum,
} from '../Components/Expense/ExpenseStatus.enum';
import ValidationModeEnum from '../Components/Expense/ValidationMode.enum';
import { ExpenseModeEnum } from '../Components/Expense/ExpenseMode.enum';
import CorrectionsAmountNoteBox from '../Components/Expense/CorrectionsAmountNoteBox';
import { ExpenseCategoryEnum } from '../Components/Expense/ExpenseCategory.enum';
import { BillTypeEnum } from '../Components/Expense/BillType.enum';
import ExpenseCorrectionDialog from '../Components/Dialog/ExpenseCorrectionDialog';
import ExpenseCorrectionAlert from '../Components/Expense/ExpenseCorrectionAlert';
import CreditDeterminationDialog from '../Components/Dialog/CreditDeterminationDialog';
import PayrollDayWarningDialog from '../Components/Dialog/PayrollDayWarningDialog';
import AssociatedInvoiceDocuments from '../Components/Expense/AssociatedInvoiceDocuments';
import { useToast } from '../../Admin/Contexts/ToastContext';
import usePayrollDayApprovalWarning from '../hooks/usePayrollDayApprovalWarning';

const styles = (theme) => ({
    ContainerRoot: {
        [theme.breakpoints.down('md')]: {
            paddingLeft: '16px',
            paddingRight: '16px',
        },
        paddingTop: '1.5rem',
        paddingBottom: '5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '40px',
    },
    formGridContainer: {
        gap: 32,
    },
    summary: {
        position: 'sticky',
        top: '5rem',
        paddingTop: '.5rem',
    },
    warningMessage: {
        color: COLORS.LT_SUNFLOWER,
        paddingTop: '0.5rem',
    },
    iconContainer: {
        position: 'absolute',
        top: 8,
        right: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        zIndex: 10,
    },
    flagIcon: {
        cursor: 'pointer',
    },
    viewBookingButton: {
        minWidth: 'auto',
        padding: '4px 8px',
        fontSize: '0.75rem',
    },
    tabPanel: {
        position: 'relative',
    },
    alertContainer: {
        marginTop: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
            marginTop: theme.spacing(4),
        },
    },
    leftIconContainer: {
        position: 'absolute',
        top: 8,
        left: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        zIndex: 10,
    },
    editButton: {
        backgroundColor: COLORS.LT_JOURNEY10,
        '&:hover': {
            backgroundColor: COLORS.LT_JOURNEY25,
        },
        color: COLORS.LT_JOURNEY,
    },
});

const defaultExpense = {
    bookingId: null,
    clinicianId: '',
    clinicianFirstName: '',
    clinicianLastName: '',
    clientId: '',
    division: '',
    state: '',
    billType: '',
    category: '',
    type: '',
    currency: 'USD',
    amount: '',
    billAmount: '',
    payAmount: '',
    payDate: null,
    reimbursedAmount: '',
    accountingProcessingNotes: '',
    label: 'Payment 1',
    status: ExpenseStatusIdsEnum.PendingSubmission,
    expenseClass: ExpenseClassEnum.Payment,
    splitTransactionComment: '',
    source: 'LtAssociate',
    vendorName: '',
    highPriority: false,
    isInvoicePriority: false,
    timelineToInvoiceExpense: null,
};

const VendorPayment = (props) => {
    const { classes } = props;
    const [isLoading, setIsLoading] = useState(false);
    const { isExpenseSystemAdministratorUser, isExpenseAdminUser, isClinicianReimEntryUser } =
        useContext(AdminAuthContext);
    const isViewOnly = !(isExpenseSystemAdministratorUser || isExpenseAdminUser);
    const [expenseCategories, setExpenseCategories] = useState(null);
    const [divisions, setDivisions] = useState([]);
    const [statusesAllowed, setStatusesAllowed] = useState([]);
    const [statusesAllowedByIndex, setStatusesAllowedByIndex] = useState({});
    const { id } = useParams();
    const history = useHistory();
    const [alert, setAlert] = useState();
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [removeTabs, setRemoveTabs] = useState(false);
    const backLink = id ? 'All Payment Request' : 'Expense Management';
    const title = id ? 'Payment Request' : 'New Payment Request';
    const expensesPath = id ? '/expenses/payment-request/review' : '/expenses';
    const [originalExpenseEntries, setOriginalExpenseEntries] = useState([]);
    const [originalPrimaryExpense, setOriginalPrimaryExpense] = useState();
    const [ocrUsageLogs, setOcrUsageLogs] = useState([]);
    const [ocrUsedValues, setUsedOcrValues] = useState([]);
    const [isCorrection, setIsCorrection] = useState(false);
    const isAnyExpensePosted = useMemo(() => {
        if (originalExpenseEntries?.length > 0) {
            return originalExpenseEntries.some(
                (entry) => entry.status === ExpenseStatusValuesEnum.Posted
            );
        }
        return false;
    }, [originalExpenseEntries]);
    const defaultValues = {
        fileUploads: [
            {
                invoice: { newFiles: [], existingFiles: [] },
                internal: { newFiles: [], existingFiles: [] },
            },
        ],
        expenseEntries: [defaultExpense],
    };
    const methods = useForm({ shouldUnregister: false, defaultValues });
    const {
        handleSubmit,
        reset,
        control,
        getValues,
        watch,
        setValue,
        setError,
        clearErrors,
        formState: { errors },
    } = methods;
    const {
        fields: expenseEntries,
        append,
        remove,
    } = useFieldArray({
        control, // control props comes from useForm (optional: if you are using FormContext)
        name: 'expenseEntries', // unique name for your Field Array
        keyName: 'expenseEntryId',
    });
    const watchExpenseEntries = watch(`expenseEntries`);
    const isOutdated =
        watchExpenseEntries?.[activeTabIndex]?.status === ExpenseStatusValuesEnum.Outdated;
    const isPageViewOnly = isViewOnly || isOutdated;
    const isInvoiceReviewLocked = isExpenseInInvoiceReview(watchExpenseEntries?.[activeTabIndex]);
    const isAnyApproved = Object.keys(watchExpenseEntries).some(
        (k) => watchExpenseEntries[k].status === ExpenseStatusValuesEnum.Approved
    );
    const [showBalanceError, setShowBalanceError] = useState(false);
    const [showBalanceWarning, setShowBalanceWarning] = useState(false);
    const [showCorrectionAmountWarning, setShowCorrectionAmountWarning] = useState(false);
    const hasErrors = !!Object.keys(methods.formState.errors).length;
    const [assignmentsByIndex, setAssignmentsByIndex] = useState({});
    const [validationMode, setValidationMode] = useState(ValidationModeEnum.Save);
    const [pendingSubmit, setPendingSubmit] = useState(false);
    const [pendingSave, setPendingSave] = useState(false);
    const [openConfirmationEditModal, setOpenConfirmationEditModal] = useState(false);
    const [editEnabledByIndex, setEditEnabledByIndex] = useState({});
    const [showAmountNoteBox, setShowAmountNoteBox] = useState(false);
    const [openCreditMemoDialog, setOpenCreditMemoDialog] = useState(false);
    const [isPatchingCreditMemo, setIsPatchingCreditMemo] = useState(false);
    const [creditMemoError, setCreditMemoError] = useState(null);
    const [creditType, setCreditType] = useState(null);
    const {
        payrollWarningOpen,
        showPayrollWarningInCmm,
        payrollWarningMessage,
        gateApproval,
        confirmPayrollWarning,
        cancelPayrollWarning,
    } = usePayrollDayApprovalWarning({ creditMemoDialogOpen: openCreditMemoDialog });
    const { showErrorToast } = useToast();

    const originalAmountTotal = useMemo(
        () =>
            originalExpenseEntries.reduce(
                (acc, expense) => acc + (Number(expense.originalReimbursedAmount) || 0),
                0
            ),
        [originalExpenseEntries]
    );
    const currentAmountTotal = useMemo(
        () => watchExpenseEntries.reduce((acc, expense) => acc + (Number(expense.amount) || 0), 0),
        [watchExpenseEntries]
    );
    const originalBillAmountTotal = useMemo(
        () =>
            originalExpenseEntries.reduce(
                (acc, expense) => acc + (Number(expense.billAmount) || 0),
                0
            ),
        [originalExpenseEntries]
    );
    const currentBillAmountTotal = useMemo(
        () =>
            watchExpenseEntries.reduce(
                (acc, expense) => acc + (Number(expense.billAmount) || 0),
                0
            ),
        [watchExpenseEntries]
    );
    const originalPayAmountTotal = useMemo(
        () =>
            originalExpenseEntries.reduce(
                (acc, expense) => acc + (Number(expense.payAmount) || 0),
                0
            ),
        [originalExpenseEntries]
    );
    const currentPayAmountTotal = useMemo(
        () =>
            watchExpenseEntries.reduce((acc, expense) => acc + (Number(expense.payAmount) || 0), 0),
        [watchExpenseEntries]
    );

    const isSupplemental = useMemo(
        () => watchExpenseEntries[activeTabIndex]?.category === ExpenseCategoryEnum.Supplemental,
        [watchExpenseEntries, activeTabIndex]
    );
    const activeExpense = useMemo(
        () =>
            Array.isArray(watchExpenseEntries) ? watchExpenseEntries[activeTabIndex] : undefined,
        [watchExpenseEntries, activeTabIndex]
    );

    // Calculate if correction amount warning should be shown
    const shouldShowCorrectionWarning =
        isCorrection &&
        (isSupplemental
            ? originalBillAmountTotal !== currentBillAmountTotal ||
              originalPayAmountTotal !== currentPayAmountTotal
            : originalAmountTotal !== currentAmountTotal);

    // Update warning state when calculation changes
    React.useEffect(() => {
        setShowCorrectionAmountWarning(shouldShowCorrectionWarning);
    }, [shouldShowCorrectionWarning]);

    const handleAssignmentChange = (index, assignment) => {
        setAssignmentsByIndex((prev) => ({
            ...prev,
            [index]: assignment,
        }));
    };

    useEffect(() => {
        Promise.all([getDivisions(), getExpenseCategories(ExpenseClassEnum.Payment)])
            .then(async ([divs, cats]) => {
                setExpenseCategories(cats);
                setDivisions(divs);
                if (!id) {
                    const statuses = await getStatusesAllowed(ExpenseClassEnum.Payment);
                    setStatusesAllowed(statuses);
                }
            })
            .catch((error) => {
                console.error(error);
            });
        if (id) {
            fetchExpenseData();
        }
    }, []);

    const fetchExpenseData = async () => {
        try {
            setIsLoading(true);
            const res = await getExpenseById(id);
            const listExpenses = [];
            const aOriginalExpenseEntries = [];
            const aFileUploads = [];
            const allowedStatusesByIdx = {};
            let selectedTabIndex = 0;

            if (res.expenseClass !== ExpenseClassEnum.Payment) {
                window.location.href = '#/404';
                showErrorToast('Payment Request not found.');
                return;
            }

            if (res.primaryExpenseId && res.primaryExpense) {
                // Child split view
                listExpenses.push(res.primaryExpense);

                if (res.primaryExpense.allowedNextStatuses) {
                    allowedStatusesByIdx[0] = res.primaryExpense.allowedNextStatuses;
                }

                for (let i = 0; i < res.primaryExpense.splitExpenses.length; i++) {
                    const listExpensesIndex = listExpenses.push(
                        res.primaryExpense.splitExpenses[i]
                    );

                    if (res.primaryExpense.splitExpenses[i].allowedNextStatuses) {
                        allowedStatusesByIdx[listExpensesIndex - 1] =
                            res.primaryExpense.splitExpenses[i].allowedNextStatuses;
                    }

                    if (Number(res.primaryExpense.splitExpenses[i].id) === Number(id)) {
                        selectedTabIndex = listExpensesIndex - 1;
                    }
                }
            } else {
                // Payment expenseVersion is unreliable (can be null or 0), so unlike the credit
                // card page we can't gate on it. Always attempt to fetch a newer (corrected)
                // version; getExpenseById 404s when none exists (handled below).
                try {
                    const newerVersion = await getExpenseById(id, true);

                    if (newerVersion?.id) {
                        res.newerVersionID = newerVersion.id;
                    }
                } catch (error) {
                    if (error.response && error.response.status !== 404) {
                        console.error('Error fetching newer version:', error);
                    }
                }
                // Parent view - push main expense first
                listExpenses.push(res);
                if (res.allowedNextStatuses) {
                    allowedStatusesByIdx[0] = res.allowedNextStatuses;
                }

                // Then push all splits
                for (let i = 0; i < res.splitExpenses.length; i++) {
                    const listExpensesIndex = listExpenses.push(res.splitExpenses[i]);
                    if (res.splitExpenses[i].allowedNextStatuses) {
                        allowedStatusesByIdx[listExpensesIndex - 1] =
                            res.splitExpenses[i].allowedNextStatuses;
                    }
                }
            }

            listExpenses[0].splitExpenses = [];

            for (let i = 0; i < listExpenses.length; i++) {
                const formattedValues = formatResponse(listExpenses[i]);
                if (_.isString(formattedValues.payDate) && formattedValues.payDate.length === 0) {
                    formattedValues.payDate = null;
                } else if (formattedValues.payDate) {
                    formattedValues.payDate = formattedValues.payDate.substring(0, 10);
                } else {
                    formattedValues.payDate = null;
                }

                aOriginalExpenseEntries.push({
                    ...formattedValues,
                    label: listExpenses[i].label ? listExpenses[i].label : `Expense ${i}`,
                });

                if (
                    listExpenses[i] &&
                    listExpenses[i].expenseImages &&
                    listExpenses[i].expenseImages.length >= 0
                ) {
                    const invoiceFiles = listExpenses[i].expenseImages.filter(
                        (file) => !file.isInternal
                    );
                    const internalFiles = listExpenses[i].expenseImages.filter(
                        (file) => file.isInternal
                    );

                    aFileUploads.push({
                        invoice: {
                            newFiles: [],
                            existingFiles: invoiceFiles,
                        },
                        internal: {
                            newFiles: [],
                            existingFiles: internalFiles,
                        },
                    });
                } else {
                    aFileUploads.push({
                        invoice: { newFiles: [], existingFiles: [] },
                        internal: { newFiles: [], existingFiles: [] },
                    });
                }
            }

            setOriginalExpenseEntries(aOriginalExpenseEntries);
            setOriginalPrimaryExpense(aOriginalExpenseEntries[0]);
            setStatusesAllowedByIndex(allowedStatusesByIdx);
            setActiveTabIndex(selectedTabIndex);

            const defaultValues = {
                fileUploads: aFileUploads,
                expenseEntries: aOriginalExpenseEntries,
            };
            reset(defaultValues);
        } catch (error) {
            console.error('Error fetching expense data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const formatResponse = (res) =>
        Object.fromEntries(
            Object.entries(res).map(([key, value]) => {
                // Preserve object structure for paymentAddress
                if (key === 'paymentAddress' && value && typeof value === 'object') {
                    return [key, value];
                }
                return [key, value === null ? '' : value];
            })
        );

    const handleAddExpense = () => {
        const parentExpense = getValues('expenseEntries[0]');

        // Clone paymentAddress object to avoid reference issues
        let clonedPaymentAddress = '';
        if (parentExpense.paymentAddress && typeof parentExpense.paymentAddress === 'object') {
            clonedPaymentAddress = { ...parentExpense.paymentAddress };
        }

        const newExpense = {
            ...parentExpense,
            label: 'New Payment',
            reimbursedAmount: '',
            billType: '',
            id: null,
            created: null,
            lastModified: null,
            status: ExpenseStatusIdsEnum.PendingSubmission,
            source: 'LtAssociate', // This will Ensure any Split from a source: 'Clinician' will be changed to 'LtAssociate'
            highPriority: false,
            isInvoicePriority: false,
            timelineToInvoiceExpense: null,
            resubmittedDate: null,
            submittedDate: null,
            mobileSubmission: false,
            salespersonName: '',
            recruiterName: '',
            amount: '',
            billAmount: '',
            payAmount: '',
            payDate: null,
            description: '',
            expenseVersion: 0,
            // Explicitly preserve payment details from parent
            vendorId: parentExpense.vendorId ?? '',
            vendorName: parentExpense.vendorName ?? '',
            vendorCity: parentExpense.vendorCity ?? '',
            vendorState: parentExpense.vendorState ?? '',
            vendorZip: parentExpense.vendorZip ?? '',
            paymentVendor: parentExpense.paymentVendor ?? '',
            paymentAddress: clonedPaymentAddress,
            vendorPayToOrderOf: parentExpense.vendorPayToOrderOf ?? '',
            paymentType: parentExpense.paymentType ?? '',
            previousVersionID: null,
        };

        // Clear bill/pay amounts for supplemental on split
        if (newExpense.category === 'Supplemental') {
            newExpense.billAmount = 0;
            newExpense.payAmount = 0;
        }

        // Create independent file uploads while preserving parent files for user decision
        const parentFileUpload = getValues('fileUploads[0]');
        const newFileUpload = {
            invoice: {
                // Clone existing database files
                existingFiles: parentFileUpload.invoice?.existingFiles
                    ? parentFileUpload.invoice.existingFiles
                          .filter((file) => !file.isCombinedPdf)
                          .map((file) => ({
                              ...file, // Create new object reference
                              expenseId: null, // Clear out the existing ExpenseId
                          }))
                    : [],
                // Clone newly uploaded files so user can decide to keep or remove
                newFiles:
                    parentFileUpload.invoice?.newFiles?.length > 0
                        ? parentFileUpload.invoice.newFiles.map((file) => {
                              const cloneNewFile = new File([file], file.name, { type: file.type });
                              Object.assign(cloneNewFile, {
                                  preview: URL.createObjectURL(file),
                                  isInternal: false,
                              });
                              return cloneNewFile;
                          })
                        : [],
            },
            internal: {
                // Clone existing database files
                existingFiles: parentFileUpload.internal?.existingFiles
                    ? parentFileUpload.internal.existingFiles
                          .filter((file) => !file.isCombinedPdf)
                          .map((file) => ({
                              ...file, // Create new object reference
                              expenseId: null, // Clear out the existing ExpenseId
                          }))
                    : [],
                // Clone newly uploaded files so user can decide to keep or remove
                newFiles:
                    parentFileUpload.internal?.newFiles?.length > 0
                        ? parentFileUpload.internal.newFiles.map((file) => {
                              const cloneNewFile = new File([file], file.name, { type: file.type });
                              Object.assign(cloneNewFile, {
                                  preview: URL.createObjectURL(file),
                                  isInternal: true,
                              });
                              return cloneNewFile;
                          })
                        : [],
            },
        };

        // Add the new file upload structure to the form
        const fileUploads = getValues(`fileUploads`);
        fileUploads.push(newFileUpload);
        setValue(`fileUploads`, fileUploads);

        // Clean up expense object
        if (newExpense.hasOwnProperty('errors')) {
            delete newExpense.errors;
        }
        if (newExpense.hasOwnProperty('statusHistory')) {
            delete newExpense.statusHistory;
        }
        if (newExpense.hasOwnProperty('relatedErrors')) {
            delete newExpense.relatedErrors;
        }
        if (newExpense.hasOwnProperty('importedData')) {
            delete newExpense.importedData;
        }
        if (!newExpense.splitTransactionComment) {
            newExpense.splitTransactionComment = '';
        }

        // Add the new expense and set it as active
        append(newExpense);
        setActiveTabIndex(expenseEntries.length);
    };

    const resetImages = (index) => {
        const fileUploads = getValues(`fileUploads`);
        fileUploads.splice(index, 1);
        setValue(`fileUploads`, fileUploads);
    };

    const handleRemoveExpense = (index) => {
        // This was added to address a rendering issue
        // It turns off the TabPanel Display removes the item and then turns back on the display
        setActiveTabIndex(index);
        setRemoveTabs(true);
    };

    function handleFlagClick(onChange, value) {
        if (isPageViewOnly) {
            return;
        }

        return onChange(!value);
    }

    useEffect(() => {
        if (removeTabs) {
            resetImages(activeTabIndex);
            remove(activeTabIndex);
            setActiveTabIndex(activeTabIndex - 1);
            setRemoveTabs(false);
        }
    }, [removeTabs]);

    const autoFillWithOCR = (ocrData) => {
        const confidenceThreshold = 0.7;
        const expenseVals = getValues(`expenseEntries[${activeTabIndex}]`);

        let usedVendorName = false;
        let usedAmount = false;
        let usedCurrency = false;

        if (
            !expenseVals.vendorName &&
            !!ocrData.vendorName?.name &&
            ocrData.vendorName?.confidence >= confidenceThreshold
        ) {
            expenseVals.vendorName = ocrData.vendorName?.name;
            usedVendorName = true;

            const ocrVal = {
                scanId: ocrData.scanId,
                vendorName: ocrData.vendorName?.name,
            };

            setUsedOcrValues((ocrUsedValues) => [...ocrUsedValues, ocrVal]);
        }

        if (
            !expenseVals.amount &&
            !!ocrData.expenseAmount?.amount &&
            ocrData.expenseAmount?.confidence >= confidenceThreshold
        ) {
            if (currencies.some((c) => c.value === ocrData.expenseAmount?.currencyCode)) {
                expenseVals.currency = ocrData.expenseAmount?.currencyCode;
                usedCurrency = true;
            } else {
                expenseVals.currency = 'USD'; // Default to USD on unsupported currency code
                usedCurrency = false;
            }

            usedAmount = true;

            const ocrVal = {
                scanId: ocrData.scanId,
                amount: ocrData.expenseAmount?.amount,
                currency: ocrData.expenseAmount?.currencyCode,
            };

            setUsedOcrValues((ocrUsedValues) => [...ocrUsedValues, ocrVal]);
        }

        const ocrUsageLogForScan = {
            expenseId: -1,
            scanId: ocrData.scanId,
            fieldUsage: {
                vendorName: usedVendorName,
                amount: usedAmount,
                currency: usedCurrency,
            },
        };

        setOcrUsageLogs((ocrUsageLogs) => [...ocrUsageLogs, ocrUsageLogForScan]);
        setValue(`expenseEntries[${activeTabIndex}]`, expenseVals);
    };

    const applySupplementalRules = (data) => {
        data.expenseEntries.forEach((entry, i) => {
            if (entry?.category !== ExpenseCategoryEnum.Supplemental) {
                setValue(`expenseEntries.${i}.billAmount`, '0');
                entry.billAmount = '0';
                setValue(`expenseEntries.${i}.payAmount`, '0');
                entry.payAmount = '0';
            }
        });
    };

    const validateAmounts = () => {
        let hasInvalidAmount = false;

        watchExpenseEntries.forEach((entry, index) => {
            const bill = Number(entry.billAmount || 0);
            const pay = Number(entry.payAmount || 0);

            const isInvalid =
                entry.category === ExpenseCategoryEnum.Supplemental && bill === 0 && pay === 0;

            if (isInvalid) {
                hasInvalidAmount = true;
                setError(`expenseEntries.${index}.billAmount`, {
                    type: 'manual',
                    message: 'Payment Amount and Bill Amount cannot both be 0',
                });
                setError(`expenseEntries.${index}.payAmount`, {
                    type: 'manual',
                    message: 'Payment Amount and Bill Amount cannot both be 0',
                });
            } else {
                clearErrors(`expenseEntries.${index}.billAmount`);
                clearErrors(`expenseEntries.${index}.payAmount`);
            }
        });

        return !hasInvalidAmount;
    };

    const validateBillAmount = () => {
        let hasInvalidAmount = false;

        watchExpenseEntries.forEach((entry, index) => {
            const bill = Number(entry.billAmount || 0);

            const isInvalid =
                entry.category === ExpenseCategoryEnum.Supplemental &&
                entry.billType === BillTypeEnum.Billable &&
                bill === 0;

            if (isInvalid) {
                hasInvalidAmount = true;
                setError(`expenseEntries.${index}.billAmount`, {
                    type: 'manual',
                    message: 'Bill Amount cannot be 0 when expense is Billable',
                });
            } else {
                clearErrors(`expenseEntries.${index}.billAmount`);
            }
        });

        return !hasInvalidAmount;
    };

    const enableCorrectionEditMode = () => {
        setOpenConfirmationEditModal(false);
        setEditEnabledByIndex((prev) => ({ ...prev, [activeTabIndex]: true }));
        const currentVersion = getValues(`expenseEntries.${activeTabIndex}.expenseVersion`);
        const updatedVersion = currentVersion ? currentVersion + 1 : 1;
        setIsCorrection(true);
        setValue(`expenseEntries.${activeTabIndex}.expenseVersion`, updatedVersion);
        setValue(`expenseEntries[${activeTabIndex}].status`, ExpenseStatusValuesEnum.InCorrection, {
            shouldDirty: true,
        });
    };

    useEffect(() => {
        setIsCorrection(
            watchExpenseEntries[activeTabIndex]?.status === ExpenseStatusValuesEnum.InCorrection
        );
    }, [watchExpenseEntries, activeTabIndex]);

    const onSubmit = async (data) => {
        try {
            applySupplementalRules(data);
            if (data.expenseEntries.some((e) => e?.category === ExpenseCategoryEnum.Supplemental)) {
                const isValid = validateAmounts() && validateBillAmount();
                if (!isValid) return;
            }

            setIsLoading(true);
            const entriesWithStatus = setStatusForEntries(
                data.expenseEntries,
                ExpenseStatusIdsEnum.Submitted
            );
            const newExpense = createExpenseFromExpenseEntries(entriesWithStatus);
            newExpense.ocrFieldUsage = getOcrUsage(newExpense);
            const expenseResponse = await createExpense(newExpense);

            const fileUploads = getValues('fileUploads').map((upload) => {
                const invoiceExistingFiles = upload.invoice?.existingFiles
                    ? [...upload.invoice.existingFiles]
                    : [];
                const invoiceNewFiles = upload.invoice?.newFiles
                    ? [...upload.invoice.newFiles]
                    : [];
                const internalExistingFiles = upload.internal?.existingFiles
                    ? [...upload.internal.existingFiles]
                    : [];
                const internalNewFiles = upload.internal?.newFiles
                    ? [...upload.internal.newFiles]
                    : [];
                return {
                    invoice: {
                        newFiles: invoiceNewFiles,
                        existingFiles: invoiceExistingFiles,
                    },
                    internal: {
                        newFiles: internalNewFiles,
                        existingFiles: internalExistingFiles,
                    },
                };
            });

            try {
                await uploadImages(fileUploads, expenseResponse, ExpenseModeEnum.Payment, true);
                history.push({
                    pathname: '/expenses/submit',
                    state: {
                        listViewUrl: '/expenses/payment-request/review',
                        newExpenseUrl: `/expenses/payment-request`,
                        type: 'submit',
                        expenseUrl: `/expenses/payment-request/${expenseResponse.id}`,
                        isViewOnly: false,
                    },
                });
            } catch (uploadError) {
                console.error('Error onSubmit Vendor Payment for image upload: ', uploadError);
                const message = `Expense was created, but image upload failed. Error message: ${formatErrorMessage(
                    uploadError
                )}`;
                setAlert({ message, type: 'error' });
            }
        } catch (error) {
            console.error('Error onSubmit Vendor Payment');
            reset({ expenseEntries: data.expenseEntries, fileUploads: data.fileUploads });
            const message = formatErrorMessage(error);
            setAlert({ message, type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const validateSubmit = async () => {
        setValidationMode(ValidationModeEnum.Submit);
        setPendingSubmit(true);
    };

    useEffect(() => {
        if (pendingSubmit && validationMode === ValidationModeEnum.Submit) {
            handleSubmit(onSubmit)();
            setPendingSubmit(false);
        }
    }, [pendingSubmit, validationMode, handleSubmit, onSubmit]);

    const validateSave = async () => {
        const isSubmitting = watchExpenseEntries.some(
            (entry) => entry.status === ExpenseStatusValuesEnum.Submitted
        );

        setValidationMode(isSubmitting ? ValidationModeEnum.Submit : ValidationModeEnum.Save);

        if (errors.balance) {
            setShowBalanceError(true);
            if (isPatchingCreditMemo) {
                setOpenCreditMemoDialog(false);
                setIsPatchingCreditMemo(false);
            }
            return;
        }

        clearErrors('balance');
        setShowBalanceError(false);
        setPendingSave(true);
    };

    const patchCreditMemo = async () => {
        try {
            const expenseId = originalExpenseEntries[activeTabIndex].id;
            setCreditMemoError(null);
            await patchExpenseCreditMemo(expenseId, creditType === 'full' ? 'Full' : 'Partial');
            history.push({
                pathname: '/expenses/submit',
                state: {
                    listViewUrl: '/expenses/payment-request/review',
                    expenseUrl: `/expenses/payment-request/${expenseId}`,
                    type: 'save',
                    isViewOnly: true,
                },
            });
        } catch (error) {
            console.error('Failed to update expense credit memo', error);
            setCreditMemoError('Failed to update expense credit memo');
            const message = `Expense was saved and image was uploaded, delta was created, but updating the credit memo failed. Error message: ${formatErrorMessage(
                error
            )}`;
            setAlert({
                message,
                type: 'error',
            });
        } finally {
            setIsPatchingCreditMemo(false);
        }
    };

    const onCloseCreditMemoDialog = (_event, reason) => {
        if (['escapeKeyDown', 'backdropClick'].includes(reason)) return;

        return setOpenCreditMemoDialog(false);
    };

    const handleConfirmCreditMemoDialog = async (creditTypeSelected) => {
        setIsPatchingCreditMemo(true);
        setCreditType(creditTypeSelected);
        validateSave();
    };

    const onSave = async (data) => {
        try {
            applySupplementalRules(data);
            if (data.expenseEntries.some((e) => e?.category === 'Supplemental')) {
                const isValid = validateAmounts() && validateBillAmount();
                if (!isValid) {
                    return;
                }
            }

            if (!isPatchingCreditMemo) setIsLoading(true);
            let entries = data.expenseEntries;
            const anyInCorrection = entries.some(
                (e) => e.status === ExpenseStatusValuesEnum.InCorrection
            );
            if (anyInCorrection) {
                entries = createCorrectionExpenseFromEntries(data.expenseEntries);
            }
            const newExpense = createExpenseFromExpenseEntries(entries);
            newExpense.ocrFieldUsage = getOcrUsage(newExpense);

            let expenseResponse;
            if (newExpense.id) {
                expenseResponse = await updateExpense(newExpense.id, newExpense);
            } else {
                expenseResponse = await createExpense(newExpense);
            }

            // Always send all files (old + new) to backend for clubbing, but split into newFiles and existingFiles for compatibility
            const fileUploads = getValues('fileUploads').map((upload) => {
                // For invoice
                const invoiceExistingFiles = upload.invoice?.existingFiles
                    ? [...upload.invoice.existingFiles]
                    : [];
                const invoiceNewFiles = upload.invoice?.newFiles
                    ? [...upload.invoice.newFiles]
                    : [];
                // For internal
                const internalExistingFiles = upload.internal?.existingFiles
                    ? [...upload.internal.existingFiles]
                    : [];
                const internalNewFiles = upload.internal?.newFiles
                    ? [...upload.internal.newFiles]
                    : [];
                return {
                    invoice: {
                        newFiles: invoiceNewFiles,
                        existingFiles: invoiceExistingFiles,
                    },
                    internal: {
                        newFiles: internalNewFiles,
                        existingFiles: internalExistingFiles,
                    },
                };
            });

            try {
                await uploadImages(fileUploads, expenseResponse, ExpenseModeEnum.Payment, true);
                try {
                    await handleSplitExpenseDeltas(
                        expenseResponse,
                        originalExpenseEntries,
                        createExpenseDelta
                    );

                    let expenseUrlId = expenseResponse.id;
                    let expenseStatus = expenseResponse.status;
                    if (activeTabIndex > 0) {
                        if (
                            newExpense.splitExpenses &&
                            newExpense.splitExpenses.length > 0 &&
                            newExpense.splitExpenses[activeTabIndex - 1]?.expenseTabName
                        ) {
                            const tabName =
                                newExpense.splitExpenses[activeTabIndex - 1].expenseTabName;
                            const splitExpense = expenseResponse.splitExpenses.find(
                                (se) => se.expenseTabName === tabName
                            );

                            expenseUrlId = splitExpense.id;
                            expenseStatus = splitExpense.status;
                        } else {
                            expenseUrlId = expenseResponse.splitExpenses[activeTabIndex - 1]?.id;
                            expenseStatus =
                                expenseResponse.splitExpenses[activeTabIndex - 1]?.status;
                        }
                    }

                    if (isPatchingCreditMemo) {
                        await patchCreditMemo();
                        return;
                    }

                    history.push({
                        pathname: '/expenses/submit',
                        state: {
                            listViewUrl: '/expenses/payment-request/review',
                            type: 'save',
                            expenseUrl: `/expenses/payment-request/${expenseUrlId}`,
                            isViewOnly: [
                                ExpenseStatusValuesEnum.Approved,
                                ExpenseStatusValuesEnum.Posted,
                                ExpenseStatusValuesEnum.Invoiced,
                            ].includes(expenseStatus),
                        },
                    });
                } catch (deltaError) {
                    console.error('Error onSave Vendor Payment for delta creation: ', deltaError);
                    const message = `Expense was saved and image was uploaded, but delta creation failed. Error message: ${formatErrorMessage(
                        deltaError
                    )}`;
                    setAlert({ message, type: 'error' });
                }
            } catch (uploadError) {
                console.error('Error onSave Vendor Payment for image upload: ', uploadError);
                const message = `Expense was saved, but image upload failed. Any new deltas were not created. Error message: ${formatErrorMessage(
                    uploadError
                )}`;
                setAlert({ message, type: 'error' });
            }
        } catch (error) {
            console.error('Error onSave Vendor Payment');
            reset({ expenseEntries: data.expenseEntries, fileUploads: data.fileUploads });
            const message = formatErrorMessage(error);
            setAlert({ message, type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!pendingSave) return;

        handleSubmit(
            (data) => {
                const intercepted = gateApproval({
                    expenses: data.expenseEntries,
                    originalExpenses: originalExpenseEntries,
                    isCreditMemoFlow: isPatchingCreditMemo,
                    proceed: () => onSave(data),
                });
                if (intercepted && isPatchingCreditMemo) {
                    setIsPatchingCreditMemo(false);
                }
            },
            () => {
                if (isPatchingCreditMemo) {
                    setOpenCreditMemoDialog(false);
                    setIsPatchingCreditMemo(false);
                }
            }
        )();
        setPendingSave(false);
        setIsCorrection(false);
    }, [pendingSave, validationMode, handleSubmit, onSave, isCorrection]);

    const getOcrUsage = (expense) => {
        const preparedOcrUsageLogs = [];

        ocrUsageLogs.forEach((usageLog) => {
            const scanUsedValues = ocrUsedValues.filter(
                (usedValue) => usedValue.scanId === usageLog.scanId
            );

            if (
                usageLog.fieldUsage.merchantName &&
                scanUsedValues.find((val) => !!val.vendorName).vendorName !== expense.vendorName
            ) {
                usageLog.fieldUsage.vendorName = false;
            }

            if (
                usageLog.fieldUsage.amount &&
                scanUsedValues.find((val) => !!val.amount).amount !== expense.amount
            ) {
                usageLog.fieldUsage.amount = false;
            }

            if (
                usageLog.fieldUsage.currency &&
                scanUsedValues.find((val) => !!val.currency).currency !== expense.currency
            ) {
                usageLog.fieldUsage.currency = false;
            }

            preparedOcrUsageLogs.push({
                ...usageLog,
                fieldUsage: JSON.stringify(usageLog.fieldUsage),
            });
        });
        return preparedOcrUsageLogs;
    };

    useEffect(() => {
        if (isCorrection) return setShowAmountNoteBox(true);

        if (
            originalExpenseEntries[activeTabIndex]?.status === ExpenseStatusValuesEnum.InCorrection
        ) {
            return setShowAmountNoteBox(true);
        }

        return setShowAmountNoteBox(false);
    }, [isCorrection, activeTabIndex, originalExpenseEntries]);

    return (
        <Container maxWidth="xl" className={classes.ContainerRoot}>
            <ValidationModeContext.Provider value={validationMode}>
                <FormProvider {...methods}>
                    <Grid2 container direction="column" spacing={2}>
                        <ExpenseEntryHeader
                            title={title}
                            backLink={backLink}
                            expensesPath={expensesPath}
                        />
                        {isLoading ? (
                            <CircularProgress color="primary" />
                        ) : (
                            <>
                                <Grid2 size={{ xs: 12 }}>
                                    <form onSubmit={handleSubmit(onSubmit)}>
                                        <Grid2 container spacing={4}>
                                            <Grid2 size={{ xs: 12, sm: 7, md: 8, lg: 9 }}>
                                                <ExpenseTabs value={activeTabIndex}>
                                                    {watchExpenseEntries.map((expense, index) => {
                                                        const tabViewOnly =
                                                            isPageViewOnly ||
                                                            (isExpenseViewOnlyByStatus(
                                                                originalExpenseEntries[index],
                                                                { isClinicianReimEntryUser }
                                                            ) &&
                                                                !editEnabledByIndex[index]);
                                                        return (
                                                            <ExpenseTab
                                                                key={index}
                                                                id={`expense-tab-${index}`}
                                                                width="100%"
                                                                label={
                                                                    <ExpenseTabLabel
                                                                        isActive={
                                                                            activeTabIndex === index
                                                                        }
                                                                        index={index}
                                                                        handleRemoveTab={() =>
                                                                            handleRemoveExpense(
                                                                                index
                                                                            )
                                                                        }
                                                                        setValue={setActiveTabIndex}
                                                                        tab={expense}
                                                                        mode={
                                                                            ExpenseModeEnum.Payment
                                                                        }
                                                                        isViewOnly={tabViewOnly}
                                                                        originalPrimaryExpense={
                                                                            originalPrimaryExpense
                                                                        }
                                                                        isAnyExpensePosted={
                                                                            isAnyExpensePosted
                                                                        }
                                                                    />
                                                                }
                                                            />
                                                        );
                                                    })}
                                                    {!isPageViewOnly &&
                                                        !(
                                                            watchExpenseEntries[0].status ===
                                                                ExpenseStatusValuesEnum.NeedMoreInformation ||
                                                            originalPrimaryExpense?.status ===
                                                                ExpenseStatusValuesEnum.NeedMoreInformation
                                                        ) && (
                                                            <AddExpenseTab
                                                                label={<AddIcon color="primary" />}
                                                                disabled={
                                                                    watchExpenseEntries[0]
                                                                        .status ===
                                                                        ExpenseStatusValuesEnum.NeedMoreInformation ||
                                                                    originalPrimaryExpense?.status ===
                                                                        ExpenseStatusValuesEnum.NeedMoreInformation ||
                                                                    watchExpenseEntries.length ===
                                                                        5 ||
                                                                    (isExpenseViewOnlyByStatus(
                                                                        activeExpense,
                                                                        { isClinicianReimEntryUser }
                                                                    ) &&
                                                                        isExpenseViewOnlyByStatus(
                                                                            originalExpenseEntries?.[
                                                                                activeTabIndex
                                                                            ],
                                                                            {
                                                                                isClinicianReimEntryUser,
                                                                            }
                                                                        ))
                                                                }
                                                                onClick={handleAddExpense}
                                                                id="expense-tab-add"
                                                            />
                                                        )}
                                                </ExpenseTabs>
                                                {!removeTabs && (
                                                    <div>
                                                        {watchExpenseEntries.map(
                                                            (expense, index) => {
                                                                const isEditEnabled = !!(
                                                                    editEnabledByIndex &&
                                                                    editEnabledByIndex[index]
                                                                );

                                                                const tabViewOnly =
                                                                    isPageViewOnly ||
                                                                    (isExpenseViewOnlyByStatus(
                                                                        originalExpenseEntries[
                                                                            index
                                                                        ],
                                                                        { isClinicianReimEntryUser }
                                                                    ) &&
                                                                        !isEditEnabled);

                                                                return (
                                                                    <ExpenseTabPanel
                                                                        className={classes.tabPanel}
                                                                        key={`${index}-panel`}
                                                                        value={activeTabIndex}
                                                                        index={index}
                                                                        id={`expense-panel-${index}`}
                                                                    >
                                                                        <Box
                                                                            className={
                                                                                classes.leftIconContainer
                                                                            }
                                                                        >
                                                                            {isExpenseViewOnlyByStatus(
                                                                                originalExpenseEntries[
                                                                                    index
                                                                                ],
                                                                                {
                                                                                    isClinicianReimEntryUser,
                                                                                }
                                                                            ) &&
                                                                                !editEnabledByIndex[
                                                                                    index
                                                                                ] &&
                                                                                !isOutdated && (
                                                                                    <Button
                                                                                        variant="contained"
                                                                                        className={
                                                                                            classes.editButton
                                                                                        }
                                                                                        color="primary"
                                                                                        onClick={() =>
                                                                                            setOpenConfirmationEditModal(
                                                                                                true
                                                                                            )
                                                                                        }
                                                                                        disabled={
                                                                                            isPageViewOnly
                                                                                        }
                                                                                    >
                                                                                        Edit
                                                                                    </Button>
                                                                                )}
                                                                        </Box>
                                                                        <Box
                                                                            className={
                                                                                classes.iconContainer
                                                                            }
                                                                        >
                                                                            <ExpenseSalesforceActionButton
                                                                                assignment={
                                                                                    assignmentsByIndex[
                                                                                        index
                                                                                    ]
                                                                                }
                                                                                expense={expense}
                                                                                originalClinicianId={
                                                                                    originalExpenseEntries[
                                                                                        index
                                                                                    ]?.clinicianId
                                                                                }
                                                                                className={
                                                                                    classes.viewBookingButton
                                                                                }
                                                                            />
                                                                            <InvoicePriorityIndicator
                                                                                isInvoicePriority={
                                                                                    expense.isInvoicePriority
                                                                                }
                                                                                timelineToInvoiceExpense={
                                                                                    expense.timelineToInvoiceExpense
                                                                                }
                                                                                fontSize="large"
                                                                            />
                                                                            <Controller
                                                                                name={`expenseEntries[${index}].highPriority`}
                                                                                control={control}
                                                                                defaultValue={
                                                                                    expense.highPriority
                                                                                }
                                                                                render={({
                                                                                    onChange,
                                                                                    value,
                                                                                }) => (
                                                                                    <FlagIcon
                                                                                        fontSize="large"
                                                                                        className={
                                                                                            classes.flagIcon
                                                                                        }
                                                                                        onClick={() =>
                                                                                            handleFlagClick(
                                                                                                onChange,
                                                                                                value
                                                                                            )
                                                                                        }
                                                                                        style={{
                                                                                            color: value
                                                                                                ? COLORS.LT_PEPPER
                                                                                                : COLORS.LT_MIDNIGHT25,
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            />
                                                                        </Box>
                                                                        <div
                                                                            className={
                                                                                classes.alertContainer
                                                                            }
                                                                        >
                                                                            <ExpenseCorrectionAlert
                                                                                expense={expense}
                                                                                mode={
                                                                                    ExpenseModeEnum.Payment
                                                                                }
                                                                            />
                                                                            {expense.errors &&
                                                                                expense.errors
                                                                                    .length > 0 && (
                                                                                    <ExpenseNeedsReviewAlert
                                                                                        expense={
                                                                                            expense
                                                                                        }
                                                                                    />
                                                                                )}
                                                                        </div>

                                                                        <Grid2
                                                                            container
                                                                            direction="column"
                                                                            className={
                                                                                classes.formGridContainer
                                                                            }
                                                                        >
                                                                            <Grid2
                                                                                size={{ xs: 12 }}
                                                                            >
                                                                                <BookingDetailsForm
                                                                                    index={index}
                                                                                    expense={
                                                                                        expense
                                                                                    }
                                                                                    originalExpenseEntries={
                                                                                        originalExpenseEntries
                                                                                    }
                                                                                    divisions={
                                                                                        divisions
                                                                                    }
                                                                                    isSGA={[]}
                                                                                    isViewOnly={
                                                                                        tabViewOnly
                                                                                    }
                                                                                    isInvoiceReviewLocked={
                                                                                        isInvoiceReviewLocked
                                                                                    }
                                                                                    onAssignmentChange={
                                                                                        handleAssignmentChange
                                                                                    }
                                                                                    mode={
                                                                                        ExpenseModeEnum.Payment
                                                                                    }
                                                                                />
                                                                            </Grid2>
                                                                            <Grid2
                                                                                size={{ xs: 12 }}
                                                                            >
                                                                                <ExpenseReceiptUploadForm
                                                                                    index={index}
                                                                                    expense={
                                                                                        expense
                                                                                    }
                                                                                    originalExpenseEntries={
                                                                                        originalExpenseEntries
                                                                                    }
                                                                                    mode={
                                                                                        ExpenseModeEnum.Payment
                                                                                    }
                                                                                    isViewOnly={
                                                                                        tabViewOnly
                                                                                    }
                                                                                    isInvoiceReviewLocked={
                                                                                        false
                                                                                    }
                                                                                    onOCRUpload={
                                                                                        autoFillWithOCR
                                                                                    }
                                                                                />
                                                                            </Grid2>
                                                                            <Grid2
                                                                                size={{ xs: 12 }}
                                                                            >
                                                                                <VendorExpenseDetailsForm
                                                                                    index={index}
                                                                                    expense={
                                                                                        expense
                                                                                    }
                                                                                    originalExpenseEntries={
                                                                                        originalExpenseEntries
                                                                                    }
                                                                                    expenseCategories={
                                                                                        expenseCategories
                                                                                    }
                                                                                    isViewOnly={
                                                                                        tabViewOnly
                                                                                    }
                                                                                    isInvoiceReviewLocked={
                                                                                        isInvoiceReviewLocked
                                                                                    }
                                                                                    mode={
                                                                                        ExpenseModeEnum.Payment
                                                                                    }
                                                                                />
                                                                            </Grid2>
                                                                            <Grid2
                                                                                size={{ xs: 12 }}
                                                                            >
                                                                                <PaymentDetailsForm
                                                                                    index={index}
                                                                                    expense={
                                                                                        expense
                                                                                    }
                                                                                    isViewOnly={
                                                                                        tabViewOnly
                                                                                    }
                                                                                    isInvoiceReviewLocked={
                                                                                        isInvoiceReviewLocked
                                                                                    }
                                                                                    originalExpenseEntries={
                                                                                        originalExpenseEntries
                                                                                    }
                                                                                />
                                                                            </Grid2>
                                                                            <Grid2
                                                                                size={{ xs: 12 }}
                                                                            >
                                                                                <InternalNotesForm
                                                                                    index={index}
                                                                                    expense={
                                                                                        expense
                                                                                    }
                                                                                    originalExpenseEntries={
                                                                                        originalExpenseEntries
                                                                                    }
                                                                                    statusesAllowed={
                                                                                        statusesAllowedByIndex[
                                                                                            index
                                                                                        ] || {}
                                                                                    }
                                                                                    isViewOnly={
                                                                                        tabViewOnly
                                                                                    }
                                                                                    numTabs={
                                                                                        watchExpenseEntries.length
                                                                                    }
                                                                                    mode={
                                                                                        ExpenseModeEnum.Payment
                                                                                    }
                                                                                    isCorrection={
                                                                                        isCorrection
                                                                                    }
                                                                                    openCreditMemoDialog={() =>
                                                                                        setOpenCreditMemoDialog(
                                                                                            true
                                                                                        )
                                                                                    }
                                                                                />
                                                                            </Grid2>
                                                                            <Grid2
                                                                                size={{ xs: 12 }}
                                                                            >
                                                                                {(() => {
                                                                                    // Calculate values needed for ExpenseReceiptUpload
                                                                                    const originalStatus =
                                                                                        expense.id &&
                                                                                        originalExpenseEntries?.length >
                                                                                            0
                                                                                            ? originalExpenseEntries.find(
                                                                                                  (
                                                                                                      entry
                                                                                                  ) =>
                                                                                                      entry.id ===
                                                                                                      expense.id
                                                                                              )
                                                                                                  ?.status ||
                                                                                              expense.status
                                                                                            : expense.status;
                                                                                    const currentStatus =
                                                                                        watchExpenseEntries[
                                                                                            index
                                                                                        ]?.status;
                                                                                    const isPosted =
                                                                                        originalStatus ===
                                                                                            ExpenseStatusValuesEnum.Posted ||
                                                                                        (currentStatus ===
                                                                                            ExpenseStatusValuesEnum.OnHoldReimbursement &&
                                                                                            originalStatus ===
                                                                                                ExpenseStatusValuesEnum.Posted);
                                                                                    const isPendingSubmission =
                                                                                        expense.status ===
                                                                                        ExpenseStatusValuesEnum.PendingSubmission;
                                                                                    const expenseId =
                                                                                        expense.id;

                                                                                    return (
                                                                                        <ExpenseReceiptUpload
                                                                                            index={
                                                                                                index
                                                                                            }
                                                                                            expense={
                                                                                                expense
                                                                                            }
                                                                                            originalExpenseEntries={
                                                                                                originalExpenseEntries
                                                                                            }
                                                                                            mode={
                                                                                                ExpenseModeEnum.Payment
                                                                                            }
                                                                                            isPosted={
                                                                                                isPosted
                                                                                            }
                                                                                            isPendingSubmission={
                                                                                                isPendingSubmission
                                                                                            }
                                                                                            isViewOnly={
                                                                                                tabViewOnly
                                                                                            }
                                                                                            onOCRUpload={
                                                                                                autoFillWithOCR
                                                                                            }
                                                                                            expenseId={
                                                                                                expenseId
                                                                                            }
                                                                                            attachmentType="internal"
                                                                                            isInternal
                                                                                            showSectionTitle
                                                                                            sectionTitle="Internal attachments"
                                                                                            sectionRequired={
                                                                                                false
                                                                                            }
                                                                                        />
                                                                                    );
                                                                                })()}
                                                                            </Grid2>
                                                                            <Grid2
                                                                                size={{ xs: 12 }}
                                                                            >
                                                                                <ReasonForSplittingForm
                                                                                    index={index}
                                                                                    expense={
                                                                                        expense
                                                                                    }
                                                                                    originalExpenseEntries={
                                                                                        originalExpenseEntries
                                                                                    }
                                                                                    isViewOnly={
                                                                                        tabViewOnly
                                                                                    }
                                                                                    mode={
                                                                                        ExpenseModeEnum.Payment
                                                                                    }
                                                                                />
                                                                            </Grid2>
                                                                        </Grid2>
                                                                    </ExpenseTabPanel>
                                                                );
                                                            }
                                                        )}
                                                    </div>
                                                )}
                                            </Grid2>
                                            <Grid2 size={{ xs: 12, sm: 5, md: 4, lg: 3 }}>
                                                <div className={classes.summary}>
                                                    <ExpenseSummary
                                                        mode={ExpenseModeEnum.Payment}
                                                        setError={setError}
                                                        cearErrors={clearErrors}
                                                        setShowBalanceError={setShowBalanceError}
                                                        showBalanceError={showBalanceError}
                                                        setShowBalanceWarning={
                                                            setShowBalanceWarning
                                                        }
                                                        isAnyApproved={isAnyApproved}
                                                    />
                                                    {showBalanceError && (
                                                        <Typography color="error">
                                                            {errors.balance.message}
                                                        </Typography>
                                                    )}
                                                    {showBalanceWarning && (
                                                        <Typography
                                                            className={classes.warningMessage}
                                                            color="inherit"
                                                        >
                                                            Warning: The check amount exceeds the
                                                            submitted amount. Please review the
                                                            values before saving or submitting.
                                                        </Typography>
                                                    )}
                                                    {showCorrectionAmountWarning && (
                                                        <Typography
                                                            className={classes.warningMessage}
                                                        >
                                                            Warning: The new payment amount
                                                            {isSupplemental
                                                                ? 's differ'
                                                                : ' differs'}{' '}
                                                            from the original. Please review the
                                                            values before saving.
                                                        </Typography>
                                                    )}
                                                    {showAmountNoteBox && (
                                                        <CorrectionsAmountNoteBox
                                                            mode={ExpenseModeEnum.Payment}
                                                            isSupplemental={isSupplemental}
                                                            originalAmount={
                                                                isSupplemental
                                                                    ? originalBillAmountTotal
                                                                    : originalAmountTotal
                                                            }
                                                            newAmount={
                                                                isSupplemental
                                                                    ? currentBillAmountTotal
                                                                    : currentAmountTotal
                                                            }
                                                            secondaryOriginalAmount={
                                                                originalPayAmountTotal
                                                            }
                                                            secondaryNewAmount={
                                                                currentPayAmountTotal
                                                            }
                                                        />
                                                    )}
                                                    {activeExpense?.id && (
                                                        <Box sx={{ marginTop: 4 }}>
                                                            <AssociatedInvoiceDocuments
                                                                expenseId={activeExpense.id}
                                                            />
                                                        </Box>
                                                    )}
                                                </div>
                                            </Grid2>
                                        </Grid2>
                                    </form>
                                </Grid2>
                                {hasErrors > 0 && (
                                    <Grid2 container className={classes.alertContainer}>
                                        <Grid2 size={{ xs: 12, sm: 7, md: 8, lg: 9 }}>
                                            <MuiAlert severity="error">
                                                Please review the highlighted fields above and
                                                correct the errors to submit your expense.
                                            </MuiAlert>
                                        </Grid2>
                                    </Grid2>
                                )}
                                <Grid2 size={{ xs: 12 }}>
                                    <ExpenseButtons
                                        validateSubmit={validateSubmit}
                                        validateSave={() => validateSave()}
                                        expensesPath={expensesPath}
                                        alwaysShowSave
                                        isViewOnly={
                                            isViewOnly ||
                                            isExpenseViewOnlyByStatus(
                                                originalExpenseEntries[activeTabIndex],
                                                { isClinicianReimEntryUser }
                                            )
                                        }
                                        enableEditByModal={
                                            !!(
                                                editEnabledByIndex &&
                                                editEnabledByIndex[activeTabIndex]
                                            )
                                        }
                                    />
                                </Grid2>
                            </>
                        )}
                    </Grid2>
                </FormProvider>
            </ValidationModeContext.Provider>
            <Alert alert={alert} setAlert={setAlert} />
            <ExpenseCorrectionDialog
                open={openConfirmationEditModal}
                onClose={() => {
                    setOpenConfirmationEditModal(false);
                    setEditEnabledByIndex((prev) => ({
                        ...prev,
                        [activeTabIndex]: false,
                    }));
                }}
                onConfirm={enableCorrectionEditMode}
                expenseId={originalExpenseEntries[activeTabIndex]?.id}
                isFullCredit={originalExpenseEntries[activeTabIndex]?.isfullCredit}
            />
            {watchExpenseEntries[activeTabIndex]?.status ===
                ExpenseStatusValuesEnum.InvoiceReview && (
                <CreditDeterminationDialog
                    open={openCreditMemoDialog}
                    onClose={onCloseCreditMemoDialog}
                    onConfirm={handleConfirmCreditMemoDialog}
                    expense={watchExpenseEntries[activeTabIndex]}
                    isLoading={isPatchingCreditMemo}
                    error={creditMemoError}
                    confirmButtonText="Save and Confirm"
                    payrollDayWarning={showPayrollWarningInCmm ? payrollWarningMessage : null}
                />
            )}
            <PayrollDayWarningDialog
                open={payrollWarningOpen}
                message={payrollWarningMessage}
                onCancel={cancelPayrollWarning}
                onConfirm={confirmPayrollWarning}
            />
        </Container>
    );
};

export default withStyles(styles)(VendorPayment);
